import { createAiUiLogger, previewText } from "./log";

type WsHandler = (msg: Record<string, unknown>) => void;

const log = createAiUiLogger("ws");

/** Avoid Chrome's "closed before the connection is established" on remount. */
function closeWebSocket(ws: WebSocket | null) {
  if (!ws) return;
  if (ws.readyState === WebSocket.CONNECTING) {
    const finish = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
    ws.addEventListener("open", finish, { once: true });
    return;
  }
  if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
}

function socketState(ws: WebSocket | null): string {
  if (!ws) return "null";
  switch (ws.readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING";
    case WebSocket.OPEN:
      return "OPEN";
    case WebSocket.CLOSING:
      return "CLOSING";
    case WebSocket.CLOSED:
      return "CLOSED";
    default:
      return String(ws.readyState);
  }
}

function toWsUrl(baseUrl: string, apiKey: string): string {
  const u = new URL(`${baseUrl.replace(/\/$/, "")}/api/v1/ws`);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.searchParams.set("apiKey", apiKey);
  return u.toString();
}

/** Derive `ws(s)://host/api/ws` from an HTTP chat URL or already-ws URL. */
export function toAiServerWsUrl(apiOrWsUrl: string, origin?: string): string {
  const raw = apiOrWsUrl.trim();
  const base =
    raw.startsWith("ws:") ||
    raw.startsWith("wss:") ||
    raw.startsWith("http:") ||
    raw.startsWith("https:")
      ? raw
      : new URL(raw || "/api/ws", origin || "http://localhost").toString();
  const u = new URL(base);
  if (u.protocol === "http:") u.protocol = "ws:";
  if (u.protocol === "https:") u.protocol = "wss:";
  const path = (u.pathname.replace(/\/$/, "") || "/");
  if (path.endsWith("/api/ws") || path === "/ws") {
    /* already a websocket path, including /p/{token}/ai/api/ws */
  } else if (path.endsWith("/api/chat")) {
    u.pathname = `${path.slice(0, -"/api/chat".length)}/api/ws`;
  } else {
    u.pathname = `${path === "/" ? "" : path}/api/ws`;
  }
  u.search = "";
  u.hash = "";
  return u.toString();
}

export type ChatRealtime = {
  stop: () => void;
};

export type ChatSocket = {
  stop: () => void;
  send: (payload: Record<string, unknown>) => boolean;
  subscribe: (fn: WsHandler) => () => void;
  isOpen: () => boolean;
  waitUntilOpen: (timeoutMs?: number) => Promise<void>;
  request: (
    payload: Record<string, unknown>,
    timeoutMs?: number
  ) => Promise<Record<string, unknown>>;
};

/**
 * One admin WebSocket for chat persist, queue updates, and live history.
 */
export function connectChatSocket(opts: {
  maintainerProUrl: string;
  apiKey: string;
  conversationExternalId?: string;
  onOpen?: () => void;
}): ChatSocket {
  let stopped = false;
  let socket: WebSocket | null = null;
  let reconnectAttempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const handlers = new Set<WsHandler>();
  const openWaiters = new Set<() => void>();

  const flushOpenWaiters = () => {
    for (const done of [...openWaiters]) {
      openWaiters.delete(done);
      done();
    }
  };

  const subscribeChannels = (ws: WebSocket, sandboxId?: string | null) => {
    const channels: string[] = [];
    if (sandboxId) channels.push(`sandbox:${sandboxId}`);
    if (opts.conversationExternalId) {
      channels.push(`conversation:${opts.conversationExternalId}`);
    }
    if (channels.length && ws.readyState === WebSocket.OPEN) {
      log.debug({ channels }, "subscribe");
      ws.send(JSON.stringify({ type: "subscribe", channels }));
    }
  };

  const connect = () => {
    if (stopped) return;
    const url = toWsUrl(opts.maintainerProUrl, opts.apiKey);
    log.info(
      {
        conversationId: opts.conversationExternalId,
        attempt: reconnectAttempt + 1,
        urlHost: (() => {
          try {
            return new URL(toWsUrl(opts.maintainerProUrl, opts.apiKey)).host;
          } catch {
            return "invalid";
          }
        })(),
      },
      "mp connecting"
    );
    const ws = new WebSocket(url);
    socket = ws;

    ws.addEventListener("open", () => {
      reconnectAttempt = 0;
      log.info({ state: socketState(ws) }, "mp open");
      subscribeChannels(ws);
      flushOpenWaiters();
      opts.onOpen?.();
    });

    ws.addEventListener("message", (event) => {
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(String(event.data)) as Record<string, unknown>;
      } catch {
        log.warn({}, "non-json message");
        return;
      }
      if (msg.type === "hello") {
        const meta =
          msg.meta && typeof msg.meta === "object"
            ? (msg.meta as Record<string, unknown>)
            : null;
        const sandboxId =
          typeof meta?.sandboxId === "string" ? meta.sandboxId : null;
        log.debug({ sandboxId }, "hello");
        subscribeChannels(ws, sandboxId);
      } else if (msg.type !== "pong") {
        log.debug(
          {
            type: msg.type,
            requestId: msg.requestId,
            conversationId: msg.conversationId ?? msg.externalId,
          },
          "message"
        );
      }
      for (const handler of [...handlers]) {
        handler(msg);
      }
    });

    ws.addEventListener("close", () => {
      if (socket === ws) socket = null;
      if (stopped) return;
      const delay = Math.min(
        30_000,
        1_000 * 2 ** Math.min(reconnectAttempt, 5)
      );
      reconnectAttempt += 1;
      log.debug({ delay, reconnectAttempt }, "close → reconnect");
      timer = setTimeout(connect, delay);
    });

    ws.addEventListener("error", () => {
      log.warn({}, "socket error");
    });
  };

  connect();

  return {
    stop: () => {
      stopped = true;
      handlers.clear();
      if (timer) clearTimeout(timer);
      log.debug({}, "stop");
      closeWebSocket(socket);
      socket = null;
    },
    send: (payload) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        log.warn(
          { type: payload.type, state: socketState(socket) },
          "mp send skipped (not open)"
        );
        return false;
      }
      try {
        log.info(
          {
            type: payload.type,
            conversationId: payload.conversationId,
            requestId: payload.requestId,
            preview: previewText(
              typeof payload.content === "string"
                ? payload.content
                : typeof payload.userMessage === "string"
                  ? payload.userMessage
                  : ""
            ),
          },
          "mp send"
        );
        socket.send(JSON.stringify(payload));
        return true;
      } catch (err) {
        log.warn({ err, type: payload.type }, "send failed");
        return false;
      }
    },
    subscribe: (fn) => {
      handlers.add(fn);
      return () => {
        handlers.delete(fn);
      };
    },
    isOpen: () => Boolean(socket && socket.readyState === WebSocket.OPEN),
    waitUntilOpen: (timeoutMs = 8_000) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        return Promise.resolve();
      }
      log.debug({ timeoutMs }, "waitUntilOpen");
      return new Promise<void>((resolve, reject) => {
        const timerId = setTimeout(() => {
          openWaiters.delete(done);
          reject(new Error("Chat WebSocket did not connect"));
        }, timeoutMs);
        const done = () => {
          clearTimeout(timerId);
          resolve();
        };
        openWaiters.add(done);
      });
    },
    request: (payload, timeoutMs = 15_000) => {
      const requestId =
        typeof payload.requestId === "string" && payload.requestId
          ? payload.requestId
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `req-${Date.now()}`;
      const body = { ...payload, requestId };
      return new Promise<Record<string, unknown>>((resolve, reject) => {
        const timerId = setTimeout(() => {
          unsub();
          reject(new Error("chat.send timed out"));
        }, timeoutMs);
        const unsub = (() => {
          const fn: WsHandler = (msg) => {
            if (msg.requestId !== requestId) return;
            if (msg.type === "chat.accepted") {
              clearTimeout(timerId);
              unsub();
              const message =
                msg.message && typeof msg.message === "object"
                  ? (msg.message as Record<string, unknown>)
                  : {};
              resolve(message);
              return;
            }
            if (msg.type === "chat.error") {
              clearTimeout(timerId);
              unsub();
              reject(
                new Error(
                  typeof msg.error === "string" && msg.error.trim()
                    ? msg.error
                    : "Failed to save message"
                )
              );
            }
          };
          handlers.add(fn);
          return () => {
            handlers.delete(fn);
          };
        })();
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          clearTimeout(timerId);
          unsub();
          reject(new Error("Chat WebSocket is not open"));
          return;
        }
        try {
          log.debug(
            {
              type: body.type,
              requestId,
              conversationId: body.conversationId,
              preview: previewText(
                typeof body.content === "string" ? body.content : ""
              ),
            },
            "request"
          );
          socket.send(JSON.stringify(body));
        } catch (err) {
          clearTimeout(timerId);
          unsub();
          reject(err instanceof Error ? err : new Error("WebSocket send failed"));
        }
      });
    },
  };
}

/**
 * WebSocket to a local ai-server (`/api/ws`) for AI turn events.
 */
export function connectAiServerSocket(opts: {
  url: string;
  conversationId?: string;
  onOpen?: () => void;
}): ChatSocket {
  const serverLog = createAiUiLogger("ws:ai-server");
  let stopped = false;
  let socket: WebSocket | null = null;
  let reconnectAttempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const handlers = new Set<WsHandler>();
  const openWaiters = new Set<() => void>();

  const flushOpenWaiters = () => {
    for (const done of [...openWaiters]) {
      openWaiters.delete(done);
      done();
    }
  };

  const subscribeConversation = (ws: WebSocket) => {
    if (!opts.conversationId || ws.readyState !== WebSocket.OPEN) return;
    serverLog.debug({ conversationId: opts.conversationId }, "subscribe");
    ws.send(
      JSON.stringify({
        type: "subscribe",
        conversationId: opts.conversationId,
        channels: [`conversation:${opts.conversationId}`],
      })
    );
  };

  const connect = () => {
    if (stopped) return;
    serverLog.info(
      {
        url: opts.url,
        conversationId: opts.conversationId,
        attempt: reconnectAttempt + 1,
      },
      "ai-server connecting"
    );
    const ws = new WebSocket(opts.url);
    socket = ws;

    ws.addEventListener("open", () => {
      reconnectAttempt = 0;
      serverLog.info({ state: socketState(ws) }, "ai-server open");
      subscribeConversation(ws);
      flushOpenWaiters();
      opts.onOpen?.();
    });

    ws.addEventListener("message", (event) => {
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(String(event.data)) as Record<string, unknown>;
      } catch {
        return;
      }
      if (msg.type === "hello") {
        serverLog.info({}, "ai-server hello");
        subscribeConversation(ws);
      } else if (msg.type !== "pong") {
        serverLog.info(
          {
            type: msg.type,
            conversationId: msg.conversationId,
            requestId: msg.requestId,
            error: msg.error,
          },
          "ai-server inbound"
        );
      }
      for (const handler of [...handlers]) {
        handler(msg);
      }
    });

    ws.addEventListener("close", () => {
      if (socket === ws) socket = null;
      if (stopped) return;
      const delay = Math.min(
        30_000,
        1_000 * 2 ** Math.min(reconnectAttempt, 5)
      );
      reconnectAttempt += 1;
      serverLog.warn({ delay, reconnectAttempt }, "ai-server close → reconnect");
      timer = setTimeout(connect, delay);
    });
  };

  connect();

  return {
    stop: () => {
      stopped = true;
      handlers.clear();
      if (timer) clearTimeout(timer);
      serverLog.debug({}, "stop");
      closeWebSocket(socket);
      socket = null;
    },
    send: (payload) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        serverLog.warn(
          { type: payload.type, state: socketState(socket) },
          "ai-server send skipped (not open)"
        );
        return false;
      }
      try {
        serverLog.info(
          {
            type: payload.type,
            conversationId: payload.conversationId,
            userMessageId: payload.userMessageId,
            preview: previewText(
              typeof payload.userMessage === "string"
                ? payload.userMessage
                : typeof payload.content === "string"
                  ? payload.content
                  : ""
            ),
          },
          "ai-server send"
        );
        socket.send(JSON.stringify(payload));
        return true;
      } catch {
        return false;
      }
    },
    subscribe: (fn) => {
      handlers.add(fn);
      return () => {
        handlers.delete(fn);
      };
    },
    isOpen: () => Boolean(socket && socket.readyState === WebSocket.OPEN),
    waitUntilOpen: (timeoutMs = 8_000) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        const timerId = setTimeout(() => {
          openWaiters.delete(done);
          reject(new Error("ai-server WebSocket did not connect"));
        }, timeoutMs);
        const done = () => {
          clearTimeout(timerId);
          resolve();
        };
        openWaiters.add(done);
      });
    },
    request: (payload, timeoutMs = 15_000) => {
      const requestId =
        typeof payload.requestId === "string" && payload.requestId
          ? payload.requestId
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `req-${Date.now()}`;
      const body = { ...payload, requestId };
      return new Promise<Record<string, unknown>>((resolve, reject) => {
        const timerId = setTimeout(() => {
          unsub();
          reject(new Error("ai-server request timed out"));
        }, timeoutMs);
        const unsub = (() => {
          const fn: WsHandler = (msg) => {
            if (msg.requestId !== requestId) return;
            if (msg.type === "chat.accepted") {
              clearTimeout(timerId);
              unsub();
              resolve(msg);
              return;
            }
            if (msg.type === "chat.error") {
              clearTimeout(timerId);
              unsub();
              reject(
                new Error(
                  typeof msg.error === "string" && msg.error.trim()
                    ? msg.error
                    : "ai-server request failed"
                )
              );
            }
          };
          handlers.add(fn);
          return () => {
            handlers.delete(fn);
          };
        })();
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          clearTimeout(timerId);
          unsub();
          reject(new Error("ai-server WebSocket is not open"));
          return;
        }
        try {
          socket.send(JSON.stringify(body));
        } catch (err) {
          clearTimeout(timerId);
          unsub();
          reject(err instanceof Error ? err : new Error("WebSocket send failed"));
        }
      });
    },
  };
}

/**
 * Subscribe to Maintainer Pro chat events.
 * Always joins the API key's sandbox channel (from hello); also joins a
 * conversation channel when an external id is provided (including "shared").
 */
export function startChatRealtime(opts: {
  maintainerProUrl: string;
  apiKey: string;
  conversationExternalId?: string;
  onMessage: WsHandler;
  onOpen?: () => void;
}): ChatRealtime {
  const socket = connectChatSocket(opts);
  const unsub = socket.subscribe(opts.onMessage);
  return {
    stop: () => {
      unsub();
      socket.stop();
    },
  };
}
